/**
 * Media type for SVG images.
 * SVG data URIs are blocked to prevent potential XSS exploits,
 * consistent with the server-side DataUriImageTagWriter security fix.
 */
const SVG_MEDIA_TYPE = "image/svg+xml";

/**
 * Matches SVG data URIs in quoted and unquoted img src attributes.
 * For unquoted attributes, the match intentionally consumes through the end of the tag's
 * attribute payload so injected attributes such as `onload=` are removed together.
 */
const SVG_DATA_URI_SRC_REGEX = /(<img\b[^>]*?\bsrc\s*=\s*)(?:(['"])\s*data:\s*image\/svg\+xml[\s\S]*?\2|(\s*data:\s*image\/svg\+xml[^>]*))/gi;

/**
 * Matches embedded style tags in EML HTML so CSS can be sanitized before the body is rendered
 * into the host reading-pane DOM.
 */
const STYLE_TAG_REGEX = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;

/**
 * Matches selectors that target the host page rather than message-scoped content.
 * These selectors can escape the message body container and restyle the reading pane itself,
 * including explicit host selectors (`html`, `body`, `:root`), the universal selector (`*`),
 * `:host`, and attribute selectors such as `[class]`.
 */
const HOST_PAGE_CSS_SELECTOR_REGEX = /(?:\bhtml\b|\bbody\b|:root|:host\b|(?:^|[\s>+~,(])\*(?=$|[\s>+~,(])|\[[^\]]+\])/i;

/**
 * Checks whether a MIME type represents SVG content.
 * Handles variations such as parameters ("image/svg+xml; charset=utf-8")
 * and leading/trailing whitespace.
 */
export const isSvgMimeType = (mimeType: string): boolean => mimeType.trim().toLowerCase().startsWith(SVG_MEDIA_TYPE);

/**
 * Strips SVG data URIs from img src attributes in HTML content.
 * Handles both quoted and unquoted src values, and rewrites matched attributes to `src=""`
 * so the image remains inert while the rest of the tag is preserved when possible.
 */
const stripSvgDataUris = (html: string): string => html.replace(SVG_DATA_URI_SRC_REGEX, "$1\"\"");

/**
 * Splits a selector list on top-level commas while ignoring commas inside strings,
 * comments, attribute selectors, and functional pseudo-class arguments.
 */
const splitSelectors = (selectorList: string): string[] => {
    const selectors: string[] = [];
    let selectorStart = 0;
    let activeQuote: string | null = null;
    let isInComment = false;
    let parenDepth = 0;
    let bracketDepth = 0;
    
    for (let index = 0; index < selectorList.length; index++) {
        const currentChar = selectorList[index];
        const nextChar = selectorList[index + 1];
        
        if (isInComment) {
            if (currentChar === "*" && nextChar === "/") {
                isInComment = false;
                index++;
            }
            
            continue;
        }
        
        if (activeQuote) {
            if (currentChar === "\\") {
                index++;
            }
            else if (currentChar === activeQuote) {
                activeQuote = null;
            }
            
            continue;
        }
        
        if (currentChar === "/" && nextChar === "*") {
            isInComment = true;
            index++;
            continue;
        }
        
        if (currentChar === "\"" || currentChar === "'") {
            activeQuote = currentChar;
            continue;
        }
        
        if (currentChar === "(") {
            parenDepth++;
            continue;
        }
        
        if (currentChar === ")") {
            parenDepth = Math.max(0, parenDepth - 1);
            continue;
        }
        
        if (currentChar === "[") {
            bracketDepth++;
            continue;
        }
        
        if (currentChar === "]") {
            bracketDepth = Math.max(0, bracketDepth - 1);
            continue;
        }
        
        if (currentChar === "," && parenDepth === 0 && bracketDepth === 0) {
            selectors.push(selectorList.slice(selectorStart, index));
            selectorStart = index + 1;
        }
    }
    
    selectors.push(selectorList.slice(selectorStart));
    return selectors;
};

/**
 * Filters a rule prelude down to only the selectors that are safe to keep in the message body.
 */
const getSanitizedRulePrelude = (rulePrelude: string): string => {
    const safeSelectors = splitSelectors(rulePrelude)
        .map(selector => selector.trim())
        .filter(Boolean)
        .filter(selector => !HOST_PAGE_CSS_SELECTOR_REGEX.test(selector));
    
    return safeSelectors.join(", ");
};

/**
 * Finds the next structural opening brace in CSS while treating quoted strings and comments
 * as inert content.
 */
const findNextStructuralOpenBrace = (cssContent: string, startIndex: number): number => {
    let activeQuote: string | null = null;
    let isInComment = false;
    
    for (let index = startIndex; index < cssContent.length; index++) {
        const currentChar = cssContent[index];
        const nextChar = cssContent[index + 1];
        
        if (isInComment) {
            if (currentChar === "*" && nextChar === "/") {
                isInComment = false;
                index++;
            }
            
            continue;
        }
        
        if (activeQuote) {
            if (currentChar === "\\") {
                index++;
            }
            else if (currentChar === activeQuote) {
                activeQuote = null;
            }
            
            continue;
        }
        
        if (currentChar === "/" && nextChar === "*") {
            isInComment = true;
            index++;
            continue;
        }
        
        if (currentChar === "\"" || currentChar === "'") {
            activeQuote = currentChar;
            continue;
        }
        
        if (currentChar === "{") {
            return index;
        }
    }
    
    return -1;
};

/**
 * Finds the matching closing brace for a CSS block while ignoring braces that appear inside
 * quoted strings or comments.
 */
const findMatchingClosingBrace = (cssContent: string, openBraceIndex: number): number => {
    let activeQuote: string | null = null;
    let isInComment = false;
    let depth = 1;
    
    for (let index = openBraceIndex + 1; index < cssContent.length; index++) {
        const currentChar = cssContent[index];
        const nextChar = cssContent[index + 1];
        
        if (isInComment) {
            if (currentChar === "*" && nextChar === "/") {
                isInComment = false;
                index++;
            }
            
            continue;
        }
        
        if (activeQuote) {
            if (currentChar === "\\") {
                index++;
            }
            else if (currentChar === activeQuote) {
                activeQuote = null;
            }
            
            continue;
        }
        
        if (currentChar === "/" && nextChar === "*") {
            isInComment = true;
            index++;
            continue;
        }
        
        if (currentChar === "\"" || currentChar === "'") {
            activeQuote = currentChar;
            continue;
        }
        
        if (currentChar === "{") {
            depth++;
        }
        else if (currentChar === "}") {
            depth--;
            
            if (depth === 0) {
                return index + 1;
            }
        }
    }
    
    return -1;
};

/**
 * Removes host-page selectors from CSS rules and preserves nested at-rules whose remaining
 * contents are still safe to keep.
 */
const stripHostPageSelectorsFromCss = (cssContent: string): string => {
    let sanitizedCss = "";
    let index = 0;
    
    while (index < cssContent.length) {
        const nextOpenBrace = findNextStructuralOpenBrace(cssContent, index);
        
        if (nextOpenBrace === -1) {
            sanitizedCss += cssContent.slice(index);
            break;
        }
        
        const rulePrelude = cssContent.slice(index, nextOpenBrace);
        const ruleEnd = findMatchingClosingBrace(cssContent, nextOpenBrace);
        
        if (ruleEnd === -1) {
            sanitizedCss += cssContent.slice(index);
            break;
        }
        
        const ruleBody = cssContent.slice(nextOpenBrace + 1, ruleEnd - 1);
        const trimmedPrelude = rulePrelude.trim();
        const isNestedRuleGroup = trimmedPrelude.startsWith("@media") ||
            trimmedPrelude.startsWith("@supports") ||
            trimmedPrelude.startsWith("@document") ||
            trimmedPrelude.startsWith("@layer") ||
            trimmedPrelude.startsWith("@container") ||
            trimmedPrelude.startsWith("@scope") ||
            trimmedPrelude.startsWith("@starting-style");
        
        if (isNestedRuleGroup) {
            const nestedCss = stripHostPageSelectorsFromCss(ruleBody).trim();
            
            if (nestedCss) {
                sanitizedCss += `${rulePrelude}{${nestedCss}}`;
            }
        }
        else {
            const sanitizedPrelude = getSanitizedRulePrelude(rulePrelude);
            
            if (sanitizedPrelude) {
                sanitizedCss += `${sanitizedPrelude}{${ruleBody}}`;
            }
        }
        
        index = ruleEnd;
    }
    
    return sanitizedCss;
};

/**
 * Removes embedded CSS rules that target the host page while preserving message-local CSS rules.
 * Empty style tags are dropped once their unsafe rules have been removed.
 */
const stripHostPageStyleRules = (html: string): string => html.replace(STYLE_TAG_REGEX, (styleTag, cssContent: string) => {
    const sanitizedCss = stripHostPageSelectorsFromCss(cssContent).trim();
    
    return sanitizedCss ? styleTag.replace(cssContent, sanitizedCss) : "";
});

/**
 * Applies EML-specific HTML hardening before the body is passed into the standard reading-pane
 * rendering path.
 */
export const sanitizeEmlHtml = (html: string): string => stripSvgDataUris(stripHostPageStyleRules(html));
